import React, { FC } from 'react';

import { Box, styled } from '@mui/material';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import FilterItem from '~/pages/DiscoverPage/components/FilterItem/FilterItem';
import { css, CssUtilityType } from '~/styled';
import { LARGE_MARGIN_PX, SKELETON_TEXT_HEIGHT_SMALL_PX, SMALL_MARGIN_PX } from '~/theme';

interface HierarchicalMenuListProps {
  isPrimary?: boolean;
  items: any[];
  refine: (value: string) => void;
  loading?: boolean;
  loadingItems?: number;
  translations?: {
    [key: string]: string;
  };
  'data-testid'?: string;
}

const HierarchicalMenuList: FC<HierarchicalMenuListProps> = ({
  items,
  refine,
  isPrimary = false,
  loading = false,
  loadingItems = 5,
  translations,
  ...rest
}) => {
  const { t } = useTenantTranslation();

  const sortedItems = items
    ?.map((item) => ({
      ...item,
      label: translations?.[item.label],
    }))
    ?.filter((item) => Boolean(item.label))
    ?.sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div>
      <Box data-testid={rest['data-testid']}>
        {loading ? (
          new Array(loadingItems)
            .fill(null)
            .map((item, i) => <LoadingFilterOptions key={i} xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />)
        ) : sortedItems && sortedItems.length > 0 ? (
          sortedItems.map((item) => (
            <ListItem
              key={item.label}
              className={!!item.data ? 'expanded' : undefined}
              optionalStyles={!!item.data ? expandedCss : undefined}
            >
              <FilterItem
                withArrow={isPrimary}
                label={item.label}
                count={item.count}
                refine={refine}
                isRefined={item.isRefined}
                filterValue={item.label}
                value={item.value}
              />
              {item.data && (
                <Sublist>
                  <HierarchicalMenuList
                    items={item.data}
                    refine={refine}
                    translations={translations}
                  />
                </Sublist>
              )}
            </ListItem>
          ))
        ) : (
          <NoItems>{t('page.discover.noFiltersAvailable')}</NoItems>
        )}
      </Box>
    </div>
  );
};

export default HierarchicalMenuList;

const LoadingFilterOptions = styled(AnimatedSkeleton)`
  margin-bottom: ${SMALL_MARGIN_PX};

  &:last-child {
    margin-bottom: 0;
  }
`;
const ListItem = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles?: CssUtilityType }>`
  &:last-child:not(.expanded) {
    & > div:first-child {
      margin-bottom: 0;
    }
  }

  ${({ optionalStyles }) => optionalStyles}
`;
const expandedCss = css`
  margin-bottom: ${SMALL_MARGIN_PX};

  &:last-child {
    margin-bottom: 0;
  }
`;
const Sublist = styled('div')`
  padding-left: ${LARGE_MARGIN_PX};
`;
const NoItems = styled('div')`
  color: ${({ theme }) => theme.palette.common.gray};
  font-size: 16px;
`;
